import type { EnableAlertDto, UserAlertDto } from "@eu.mdotm.api/sphere";
import { reportPlatformError } from "$root/api/error-reporting";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { IconWalls } from "$root/components/IconWall";
import { PageHeader } from "$root/components/PageHeader";
import NotificationSettings from "$root/functional-areas/notification-settings";
import { type NotificationMap, notificationMap } from "$root/functional-areas/notification-settings/map";
import { notificationSettingsRepository } from "$root/functional-areas/notification-settings/repository";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { customObjectEntriesFn, useTaskQueueWithAtom } from "$root/utils";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { ProgressBar } from "@mdotm/shadcn/legacy/components";
import { useCallback, useMemo } from "react";

function buildPayload(items: NotificationMap): EnableAlertDto[] {
	return customObjectEntriesFn(items).map(
		([key, value]) =>
			({
				enabled: value.checked,
				name: key,
				threshold: value.threshold,
				type: value.type,
			}) satisfies EnableAlertDto,
	);
}

export function updateNotification(node: UserAlertDto, map: NotificationMap): NotificationMap {
	if (!node.children || node.children.length === 0) {
		return map;
	}

	node.children.forEach((childNode) => {
		if (childNode.name && map[childNode.name as keyof typeof map]) {
			map[childNode.name as keyof typeof map] = {
				checked: childNode.enable ?? false,
				threshold: childNode.threshold ?? map[childNode.name as keyof typeof map].threshold,
				type: childNode.type ?? map[childNode.name as keyof typeof map].type,
			};
			updateNotification(childNode, map);
		} else {
			updateNotification(childNode, map);
		}
	});

	return map;
}

const Notification = (): React.JSX.Element => {
	const crumbs = useCrumbs();
	const [updateQueueState$, updateQueue] = useTaskQueueWithAtom();

	const notificationSettingsQuery = notificationSettingsRepository.notificationSettings.useQuery();
	useQueryReactor(notificationSettingsQuery, {
		onError: (err) =>
			reportPlatformError(err, "ERROR", "alerts", { message: "unable to retrieve notification settings" }),
	});

	const { isError, isLoading, data } = notificationSettingsQuery;

	const remapNotification = useMemo(() => {
		const clonedNotificationMap = { ...notificationMap } satisfies NotificationMap;

		if (!data) {
			return clonedNotificationMap;
		}
		return updateNotification(data, clonedNotificationMap);
	}, [data]);

	const onSettingChange = useCallback(
		(items: NotificationMap) => {
			updateQueue.enqueue(() =>
				runWithErrorReporting(
					async () => {
						const payload = buildPayload(items);
						await notificationSettingsRepository.editAlerts(payload);
						trackMixPanelEvent("notificationSettings.alertsUpdated", payload);
					},
					{
						area: "alerts",
						attemptedOperation: {
							message: "failed to update notification settings",
							payload: JSON.stringify(items),
						},
					},
				),
			);
		},
		[updateQueue],
	);

	return (
		<>
			<PageHeader crumbs={crumbs.mySettings("Notification settings")} />
			{isLoading && <ProgressBar value="indeterminate" />}
			{isError || isLoading ? (
				<div className="flex flex-1 h-[80vh]">
					{isLoading ? (
						<IconWalls.LoadingData opaque />
					) : (
						<IconWalls.ErrorData opaque redirect={{ title: "Home", path: "/" }} />
					)}
				</div>
			) : (
				<NotificationSettings
					notifications={remapNotification}
					onSettingChange={onSettingChange}
					updateQueueState$={updateQueueState$}
				/>
			)}
		</>
	);
};

export default Notification;
